import React, { useMemo } from "react";
import RequestOtherDeliveryDatesCalendar from "./RequestOtherDeliveryDatesCalendar";
import { getUsDateString } from "../../../utils/dateHelpers";
import { Button } from "@ds";
import { ButtonSize, ButtonStyle, ButtonVariant } from "@ds/elements/Button/Button.types";
import styles from "./RequestOtherDeliveryDates.module.scss";

interface IRequestOtherDeliveryDatesProps {
  shouldShowCalendar: boolean;
  isSucceedToConfirmDeliveryDate: boolean;
  deliveryToDate: Date;
  deliveryFromDate: Date;
  requestedFromDate: Date | null;
  requestedToDate: Date | null;
  onSubmitChangeDeliveryDate: () => void;
  onCancelRequestOtherDate: () => void;
  onRequestOtherDateClick: () => void;
  onDateRangeChange: (fromDate: Date | null, toDate: Date | null) => void;
}

const RequestOtherDeliveryDates: React.FC<IRequestOtherDeliveryDatesProps> = ({
  shouldShowCalendar,
  isSucceedToConfirmDeliveryDate,
  deliveryToDate,
  deliveryFromDate,
  requestedFromDate,
  requestedToDate,
  onSubmitChangeDeliveryDate,
  onCancelRequestOtherDate,
  onRequestOtherDateClick,
  onDateRangeChange,
}) => {
  const message = useMemo(() => {
    const startDateStr = getUsDateString(deliveryFromDate);
    const endDateStr = getUsDateString(deliveryToDate);

    const tailMessage = shouldShowCalendar
      ? "If you are not available during those dates, please request another time frame."
      : isSucceedToConfirmDeliveryDate
      ? ""
      : "You will soon receive packet tracking information via email. " +
        "If you are not available during those dates, please request a later time frame.";

    return (
      <span>
        Your approval notices will be delivered to the above address
        approximately between{" "}
        <b>
          {startDateStr} to {endDateStr}.
        </b>{" "}
        {tailMessage}
      </span>
    );
  }, [
    shouldShowCalendar,
    isSucceedToConfirmDeliveryDate,
    deliveryToDate,
    deliveryFromDate,
  ]);

  const shouldEnableUpdateRequestDeliveryDate = useMemo(
    () => !!requestedFromDate && !!requestedToDate,
    [requestedFromDate, requestedToDate]
  );

  const label = useMemo(() => {
    if (shouldEnableUpdateRequestDeliveryDate) {
      const startDateStr = getUsDateString(requestedFromDate!);
      const endDateStr = getUsDateString(requestedToDate!);
      return (
        <span className={styles['delivery-date-green']}>
          Delivery between {startDateStr} to {endDateStr}
        </span>
      );
    }
    return (
      <span className={styles['delivery-date-note']}>
        This is required only if you're unavailable between the default dates
      </span>
    );
  }, [
    shouldEnableUpdateRequestDeliveryDate,
    requestedFromDate,
    requestedToDate,
  ]);
  
  return (
    <div className="mac-layout">
      <div>{message}</div>
      {shouldShowCalendar && (
        <>
          <div className="mac-delivery-calendar-wrapper">
            <RequestOtherDeliveryDatesCalendar
              fromDate={deliveryFromDate}
              toDate={deliveryToDate}
              requestFromDate={requestedFromDate}
              requestToDate={requestedToDate}
              onDateRangeChange={onDateRangeChange}
            />
            {label}
          </div>
          <div className={styles['actions']}>
            <Button
              styleType={ButtonStyle.secondary}
              variant={ButtonVariant.outlined}
              size={ButtonSize.large}
              onClick={onSubmitChangeDeliveryDate}
              disabled={!shouldEnableUpdateRequestDeliveryDate}
            >
              CONFIRM DATES
            </Button>
            <Button
              styleType={ButtonStyle.danger}
              variant={ButtonVariant.outlined}
              size={ButtonSize.large}
              onClick={onCancelRequestOtherDate}
            >
              CANCEL
            </Button>
          </div>
        </>
      )}
      {!shouldShowCalendar && !isSucceedToConfirmDeliveryDate && (
        <div>
          <Button
            styleType={ButtonStyle.secondary}
            variant={ButtonVariant.outlined}
            onClick={onRequestOtherDateClick}
            size={ButtonSize.large}
            className={styles['request-other-dates-button']}
          >
            REQUEST OTHER DATES
          </Button>
        </div>
      )}
    </div>
  );
};

export default RequestOtherDeliveryDates;